<script setup lang="ts">
import { ref } from 'vue'

import type { ContentfulNavigationItem } from '@fondof-ui/types'
import { localizeUrl } from '@shared/fondof-fn/url/localize'
import { useSearchStore } from '@shared/global-store/stores/search'
import CloseSearchButton from '@storefront-satch/components/molecules/search/CloseSearchButton.vue'
import Search from '@storefront-satch/components/molecules/search/Search.vue'
import SearchDropdown from '@storefront-satch/components/molecules/search/SearchDropdown.vue'
import SearchToggle from '@storefront-satch/components/molecules/search/SearchToggle.vue'
import HeaderNav from '@storefront-satch/components/organisms/navigation/HeaderNav.vue'
import MenuClosedTranslatedIcon from '@storefront-satch/components/organisms/navigation/icon/MenuClosedTranslatedIcon.vue'
import MenuOpenedTranslatedIcon from '@storefront-satch/components/organisms/navigation/icon/MenuOpenedTranslatedIcon.vue'
import NavigationCart from '@storefront-satch/components/organisms/navigation/meta/NavigationCart.vue'
import NavigationWishlist from '@storefront-satch/components/organisms/navigation/meta/NavigationWishlist.vue'
import Storefinder from '@storefront-satch/components/organisms/navigation/meta/Storefinder.vue'
import NavigationAuth from '@storefront-satch/components/organisms/navigation/meta/account/NavigationAuth.vue'
import config from '@storefront-satch/config'

import {
  AffenzahnIcon,
  AlviIcon,
  ErgobagIcon,
  SatchIcon,
} from '@fond-of/fondof-icons'

const isMobileNavOpen = ref(false)

const props = defineProps<{
  links: ContentfulNavigationItem[]
  storefinder: {
    url: string
    label: string
  }
  authorizationRedirectUri: string
}>()

const updateMobileNav = () => {
  isMobileNavOpen.value = !isMobileNavOpen.value

  if (isMobileNavOpen.value) {
    document.querySelector('body')?.classList.add('!overflow-hidden')
  } else {
    document.querySelector('body')?.classList.remove('!overflow-hidden')
  }
}

const searchStore = useSearchStore()
</script>

<template>
  <div
    v-if="isMobileNavOpen"
    class="z-mobile-navigation visible inset-0 bg-white opacity-80 lg:hidden"
    aria-label="Close menu"
    role="button"
    @click="updateMobileNav()"
  />

  <div class="z-header-wrapper inset-x-0 top-0 isolate">
    <div class="bg-gray-nav relative hidden text-black lg:block">
      <div
        class="max:px-[160px] lg:max-w-screen-max relative flex items-center justify-between py-1 lg:mx-auto lg:px-[100px] xl:px-[120px]"
      >
        <div class="flex items-center space-x-5">
          <a
            class="text-gray-icon"
            :href="`${config.crossbrand.alvi.url}${config.crossbrand.utmParamsHeader || ''}`"
          >
            <AlviIcon
              height="20px"
              width="38px"
              class="pb-[2px]"
            />
          </a>
          <a
            class="text-gray-icon"
            :href="`${config.crossbrand.affenzahn.url}${config.crossbrand.utmParamsHeader || ''}`"
          >
            <AffenzahnIcon
              height="20px"
              width="57px"
            />
          </a>

          <a
            class="[&_path]:fill-gray-icon"
            :href="`${config.crossbrand.ergobag.url}${config.crossbrand.utmParamsHeader || ''}`"
          >
            <ErgobagIcon
              height="18px"
              width="48px"
            />
          </a>
        </div>

        <div class="text-gray-topnav relative flex space-x-8">
          <Storefinder
            :href="props.storefinder.url"
            :label="props.storefinder.label"
          />
          <NavigationWishlist
            :authorization-redirect-uri="props.authorizationRedirectUri"
          />
          <NavigationAuth
            :authorization-redirect-uri="props.authorizationRedirectUri"
          />
          <NavigationCart />
        </div>
      </div>
    </div>

    <div
      class="z-header relative bg-white"
      :class="{ 'header--active': isMobileNavOpen }"
    >
      <div
        class="max:px-[160px] lg:max-w-screen-max flex h-[50px] items-center justify-between px-5 lg:mx-auto lg:h-[73px] lg:px-[100px] xl:px-[120px]"
      >
        <div class="flex-initial pt-1 lg:pt-2">
          <a
            class="text-black"
            :href="localizeUrl('/')"
          >
            <SatchIcon class="h-7 md:h-11" />
          </a>
        </div>

        <div class="w-full flex-auto text-right lg:block">
          <HeaderNav
            v-if="props.links"
            :links="props.links"
            :authorization-redirect-uri="props.authorizationRedirectUri"
            :is-mobile-open="isMobileNavOpen"
            :storefinder="props.storefinder"
          />
        </div>

        <div class="flex flex-1 justify-end space-x-[1.563rem]">
          <SearchToggle
            @update-mobile-nav="
              () => {
                if (isMobileNavOpen) {
                  updateMobileNav()
                }
              }
            "
          />

          <NavigationAuth
            :authorization-redirect-uri="props.authorizationRedirectUri"
            class="lg:hidden"
          />
          <NavigationCart class="size-5.5 lg:hidden" />

          <button
            class="size-5.5 cursor-pointer border-0 p-0 text-black lg:hidden"
            role="button"
            @click="updateMobileNav()"
          >
            <MenuOpenedTranslatedIcon
              v-if="isMobileNavOpen"
              width="22px"
              height="22px"
              aria-label="Close menu"
            />
            <MenuClosedTranslatedIcon
              v-else
              width="22px"
              height="22px"
              aria-label="Open menu"
            />
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="searchStore.isSearchOpen"
      data-slot="navigation"
      class="group/search-bar border-t-gray-ultra-light z-search absolute isolate inline-flex w-full items-center justify-center bg-white shadow-lg sm:px-5 md:border-t md:py-5"
    >
      <div
        :class="[
          'group/search-form relative w-full max-w-[582px]',
          {
            'has-[:focus]:bg-white': searchStore.showSuggestions,
          },
        ]"
      >
        <div
          class="flex items-center justify-center px-5 py-2.5 sm:pt-1 md:px-0 md:py-0"
        >
          <Search id="navigation-search-input" />
          <CloseSearchButton class="md:hidden" />
        </div>
        <div
          class="w-full pr-8 shadow-lg sm:pl-[18px] sm:pr-[66px] md:absolute md:p-0"
        >
          <SearchDropdown
            data-slot="navigation"
            class="-mt-1.5 hidden group-has-[:focus-within:not(:placeholder-shown)]/search-bar:block md:mt-1"
          />
        </div>
      </div>
      <CloseSearchButton class="hidden md:block" />
    </div>
  </div>
</template>
